import React, { useState, useRef } from 'react';
import { X, Download, Upload, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { ISOControl, Task, TechnicalReferent } from '../../types/iso';

// =====================================================
// TIPOS INTERNOS
// =====================================================

interface ParsedRow {
  rowNum: number;
  codigo_control: string;
  resolvedControl?: ISOControl;
  titulo: string;
  descripcion: string;
  email_responsable: string;
  fecha_vencimiento: string;
  prioridad: 'low' | 'medium' | 'high' | 'critical';
  errors: string[];
  referent?: TechnicalReferent;
}

interface ImportResult {
  rowNum: number;
  titulo: string;
  codigo_control: string;
  success: boolean;
  error?: string;
}

type Step = 'upload' | 'preview' | 'importing' | 'done';

// =====================================================
// PROPS
// =====================================================

interface GlobalBulkTaskUploadModalProps {
  onClose: () => void;
  controls: ISOControl[];
  technicalReferents: TechnicalReferent[];
  addTaskToControl: (
    controlId: string,
    task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
  ) => Promise<void>;
  toast?: {
    success: (message: string, description?: string) => void;
    error: (message: string, description?: string) => void;
    warning: (message: string, description?: string) => void;
    info: (message: string, description?: string) => void;
  };
}

// =====================================================
// HELPERS
// =====================================================

/** Parsea una línea CSV respetando comillas dobles */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

/** Normaliza prioridad: acepta español e inglés */
function normalizePriority(raw: string): 'low' | 'medium' | 'high' | 'critical' | null {
  const map: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
    low: 'low', baja: 'low',
    medium: 'medium', media: 'medium', normal: 'medium',
    high: 'high', alta: 'high',
    critical: 'critical', crítica: 'critical', critica: 'critical',
  };
  return map[raw.toLowerCase()] ?? null;
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function GlobalBulkTaskUploadModal({
  onClose,
  controls,
  technicalReferents,
  addTaskToControl,
  toast,
}: GlobalBulkTaskUploadModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ----------------------
  // Descargar plantilla
  // ----------------------
  const downloadTemplate = () => {
    const headers = 'codigo_control,titulo,descripcion,email_responsable,fecha_vencimiento,prioridad';
    const ex1 = '"A.5.1","Implementar política de contraseñas","Crear y documentar la política de contraseñas seguras","responsable@empresa.com","2026-06-01","high"';
    const ex2 = '"A.8.2","Revisar clasificación de activos","Auditar y clasificar todos los activos de información","otroresponsable@empresa.com","2026-06-15","medium"';
    const csv = [headers, ex1, ex2].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'plantilla_tareas_global_iso27002.csv';
    link.click();
    URL.revokeObjectURL(url);
    console.log('📥 [UI] Plantilla CSV global descargada');
  };

  // ----------------------
  // Procesar archivo CSV
  // ----------------------
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) {
        setParseError('El archivo está vacío.');
        return;
      }

      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) {
        setParseError('El archivo debe tener al menos una fila de datos además del encabezado.');
        return;
      }

      // Validar encabezados
      const headerLine = lines[0].toLowerCase().replace(/"/g, '');
      const expectedHeaders = ['codigo_control', 'titulo', 'descripcion', 'email_responsable', 'fecha_vencimiento', 'prioridad'];
      const missing = expectedHeaders.filter((h) => !headerLine.includes(h));
      if (missing.length > 0) {
        setParseError(`Faltan columnas requeridas en el encabezado: ${missing.join(', ')}`);
        return;
      }

      // Mapear índices de columna
      const headerCols = parseCSVLine(lines[0].toLowerCase().replace(/"/g, ''));
      const idx = {
        codigo: headerCols.indexOf('codigo_control'),
        titulo: headerCols.indexOf('titulo'),
        descripcion: headerCols.indexOf('descripcion'),
        email: headerCols.indexOf('email_responsable'),
        fecha: headerCols.indexOf('fecha_vencimiento'),
        prioridad: headerCols.indexOf('prioridad'),
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const rows: ParsedRow[] = lines.slice(1).map((line, i) => {
        const cols = parseCSVLine(line);
        const rowNum = i + 2;
        const errors: string[] = [];

        const codigoRaw = (cols[idx.codigo] ?? '').trim();
        const titulo = (cols[idx.titulo] ?? '').trim();
        const descripcion = (cols[idx.descripcion] ?? '').trim();
        const emailRaw = (cols[idx.email] ?? '').trim().toLowerCase();
        const fechaRaw = (cols[idx.fecha] ?? '').trim();
        const prioridadRaw = (cols[idx.prioridad] ?? '').trim();

        // Validar código de control (acepta "A.8.34" y "8.34")
        let resolvedControl: ISOControl | undefined;
        if (!codigoRaw) {
          errors.push('El código de control es requerido.');
        } else {
          const normalizedCodigo = codigoRaw.replace(/^A\./i, '');
          resolvedControl = controls.find(
            (c) => c.code.toLowerCase() === normalizedCodigo.toLowerCase()
                || c.code.toLowerCase() === codigoRaw.toLowerCase()
          );
          if (!resolvedControl) {
            errors.push(`No existe control ISO 27002 con código "${codigoRaw}".`);
          }
        }

        // Validar título
        if (!titulo) {
          errors.push('El título es requerido.');
        } else if (titulo.length < 3) {
          errors.push('El título debe tener al menos 3 caracteres.');
        }

        // Validar email y buscar referente
        let referent: TechnicalReferent | undefined;
        if (!emailRaw) {
          errors.push('El email del responsable es requerido.');
        } else {
          referent = technicalReferents.find((r) => r.email.toLowerCase() === emailRaw);
          if (!referent) {
            errors.push(`No se encontró un referente activo con email "${emailRaw}".`);
          }
        }

        // Validar fecha
        if (!fechaRaw) {
          errors.push('La fecha de vencimiento es requerida.');
        } else {
          const date = new Date(fechaRaw);
          if (isNaN(date.getTime())) {
            errors.push('Formato de fecha inválido. Use YYYY-MM-DD.');
          } else if (date < today) {
            errors.push('La fecha de vencimiento no puede ser anterior a hoy.');
          }
        }

        // Validar/normalizar prioridad
        const prioridad = prioridadRaw
          ? normalizePriority(prioridadRaw) ?? 'medium'
          : 'medium';
        if (prioridadRaw && !normalizePriority(prioridadRaw)) {
          errors.push(`Prioridad "${prioridadRaw}" no válida. Use: low, medium, high, critical.`);
        }

        return {
          rowNum,
          codigo_control: codigoRaw,
          resolvedControl,
          titulo,
          descripcion,
          email_responsable: emailRaw,
          fecha_vencimiento: fechaRaw,
          prioridad,
          errors,
          referent,
        };
      });

      setParsedRows(rows);
      setStep('preview');
      console.log(`📋 [UI] CSV global parseado: ${rows.length} filas (${rows.filter((r) => r.errors.length === 0).length} válidas)`);
    };

    reader.readAsText(file, 'UTF-8');
  };

  // ----------------------
  // Importar tareas
  // ----------------------
  const handleImport = async () => {
    const validRows = parsedRows.filter((r) => r.errors.length === 0 && r.referent && r.resolvedControl);
    if (validRows.length === 0) return;

    setStep('importing');
    setImportProgress(0);
    const results: ImportResult[] = [];

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      setImportProgress(i + 1);

      try {
        const taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'> = {
          title: row.titulo,
          description: row.descripcion,
          status: 'pending',
          assignee: row.referent!,
          dueDate: row.fecha_vencimiento,
          completedAt: undefined,
          checklist: [],
          priority: row.prioridad,
          estimatedHours: 0,
          actualHours: 0,
          emailSent: false,
        };
        await addTaskToControl(row.resolvedControl!.id, taskData);
        results.push({ rowNum: row.rowNum, titulo: row.titulo, codigo_control: row.codigo_control, success: true });
        console.log(`✅ [UI] Tarea global importada: "${row.titulo}" → ${row.codigo_control} (fila ${row.rowNum})`);
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'Error desconocido';
        results.push({ rowNum: row.rowNum, titulo: row.titulo, codigo_control: row.codigo_control, success: false, error: errMsg });
        console.error(`❌ [UI] Error importando fila ${row.rowNum}:`, err);
      }
    }

    setImportResults(results);
    setStep('done');

    const successCount = results.filter((r) => r.success).length;
    const errorCount = results.filter((r) => !r.success).length;

    if (toast) {
      if (errorCount === 0) {
        toast.success(
          `${successCount} tarea${successCount !== 1 ? 's' : ''} importada${successCount !== 1 ? 's' : ''} exitosamente`,
          'Carga masiva global completada'
        );
      } else {
        toast.warning(
          `${successCount} importadas, ${errorCount} con errores`,
          'Carga masiva global — Revise el resumen'
        );
      }
    }
  };

  // ----------------------
  // Valores derivados
  // ----------------------
  const validCount = parsedRows.filter((r) => r.errors.length === 0).length;
  const errorCount = parsedRows.filter((r) => r.errors.length > 0).length;

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={step === 'importing' ? undefined : onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Carga Masiva Global de Tareas — ISO 27002
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Importa tareas para múltiples controles a la vez mediante CSV
            </p>
          </div>
          {step !== 'importing' && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Cerrar modal"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>

        {/* Indicador de paso */}
        <div className="px-5 pt-4 flex-shrink-0">
          <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
            {(['upload', 'preview', 'importing', 'done'] as Step[]).map((s, i) => {
              const labels: Record<Step, string> = {
                upload: '1. Subir CSV',
                preview: '2. Previsualizar',
                importing: '3. Importando',
                done: '4. Resumen',
              };
              const isActive = step === s;
              const isPast =
                (['upload', 'preview', 'importing', 'done'] as Step[]).indexOf(step) > i;
              return (
                <React.Fragment key={s}>
                  {i > 0 && <span>›</span>}
                  <span
                    className={
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                        : isPast
                        ? 'text-green-600 dark:text-green-400'
                        : ''
                    }
                  >
                    {labels[s]}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* ---- STEP: upload ---- */}
          {step === 'upload' && (
            <div className="space-y-4">
              {/* Descargar plantilla */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Paso 1: Descarga la plantilla
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  La plantilla incluye la columna <span className="font-mono text-blue-600 dark:text-blue-400">codigo_control</span> para especificar a qué control corresponde cada tarea.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar Plantilla CSV</span>
                </button>
              </div>

              {/* Descripción de columnas */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Formato del CSV
                </h3>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="pb-1 pr-3">Columna</th>
                      <th className="pb-1 pr-3">Requerido</th>
                      <th className="pb-1">Descripción</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-gray-300">
                    {[
                      { col: 'codigo_control', req: true, desc: 'Código del control ISO 27002 (ej: A.5.1, A.8.2)' },
                      { col: 'titulo', req: true, desc: 'Título de la tarea (mín. 3 caracteres)' },
                      { col: 'descripcion', req: false, desc: 'Descripción detallada (opcional)' },
                      { col: 'email_responsable', req: true, desc: 'Email de un referente técnico activo' },
                      { col: 'fecha_vencimiento', req: true, desc: 'Fecha límite en formato YYYY-MM-DD' },
                      { col: 'prioridad', req: false, desc: 'low / medium / high / critical (default: medium)' },
                    ].map(({ col, req, desc }) => (
                      <tr key={col} className="border-b border-gray-50 dark:border-gray-700/50">
                        <td className="py-1 pr-3 font-mono text-blue-600 dark:text-blue-400">{col}</td>
                        <td className="py-1 pr-3">
                          {req ? (
                            <span className="text-red-500">✓ Sí</span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                        <td className="py-1">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Referentes disponibles */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Referentes técnicos disponibles ({technicalReferents.length})
                </h3>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {technicalReferents.map((r) => (
                    <div key={r.id} className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-800 dark:text-gray-200">{r.name}</span>
                      <span>—</span>
                      <span className="font-mono text-blue-600 dark:text-blue-400">{r.email}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Input de archivo */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Paso 2: Sube tu archivo CSV
                </h3>
                {parseError && (
                  <p className="text-xs text-red-600 dark:text-red-400 mb-3 flex items-center justify-center space-x-1">
                    <AlertCircle className="w-3 h-3" />
                    <span>{parseError}</span>
                  </p>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center space-x-2 mx-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <span>Seleccionar archivo CSV</span>
                </button>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Solo archivos .csv</p>
              </div>
            </div>
          )}

          {/* ---- STEP: preview ---- */}
          {step === 'preview' && (
            <div className="space-y-3">
              <div className="flex items-center space-x-4 text-sm">
                <span className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>{validCount} fila{validCount !== 1 ? 's' : ''} válida{validCount !== 1 ? 's' : ''}</span>
                </span>
                {errorCount > 0 && (
                  <span className="flex items-center space-x-1 text-red-500">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errorCount} con errores (no se importarán)</span>
                  </span>
                )}
              </div>

              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr className="text-left text-gray-500 dark:text-gray-400">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Control</th>
                      <th className="px-3 py-2">Título</th>
                      <th className="px-3 py-2">Responsable</th>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">Prioridad</th>
                      <th className="px-3 py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((row) => {
                      const isValid = row.errors.length === 0;
                      return (
                        <tr
                          key={row.rowNum}
                          className={`border-t border-gray-100 dark:border-gray-700 ${
                            isValid
                              ? 'bg-green-50/40 dark:bg-green-900/10'
                              : 'bg-red-50/40 dark:bg-red-900/10'
                          }`}
                        >
                          <td className="px-3 py-2 text-gray-400">{row.rowNum}</td>
                          <td className="px-3 py-2">
                            {row.resolvedControl ? (
                              <span className="font-mono text-blue-600 dark:text-blue-400">{row.resolvedControl.code}</span>
                            ) : (
                              <span className="text-red-500 font-mono">{row.codigo_control || '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-900 dark:text-white max-w-[140px] truncate" title={row.titulo}>
                            {row.titulo || <span className="italic text-gray-400">vacío</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                            {row.referent?.name ?? (
                              <span className="text-red-500">{row.email_responsable || '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.fecha_vencimiento || '—'}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.prioridad}</td>
                          <td className="px-3 py-2">
                            {isValid ? (
                              <span className="text-green-600 dark:text-green-400 flex items-center space-x-1">
                                <CheckCircle className="w-3 h-3" />
                                <span>OK</span>
                              </span>
                            ) : (
                              <span className="text-red-500" title={row.errors.join('\n')}>
                                <AlertCircle className="w-3 h-3 inline mr-1" />
                                {row.errors[0]}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ---- STEP: importing ---- */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Importando tarea {importProgress} de {parsedRows.filter((r) => r.errors.length === 0).length}...
              </p>
              <div className="w-full max-w-sm bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${(importProgress / parsedRows.filter((r) => r.errors.length === 0).length) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* ---- STEP: done ---- */}
          {step === 'done' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4 text-sm">
                <span className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>{importResults.filter((r) => r.success).length} tareas importadas</span>
                </span>
                {importResults.filter((r) => !r.success).length > 0 && (
                  <span className="flex items-center space-x-1 text-red-500">
                    <AlertCircle className="w-4 h-4" />
                    <span>{importResults.filter((r) => !r.success).length} con error</span>
                  </span>
                )}
              </div>

              {importResults.filter((r) => !r.success).length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-2">Errores durante la importación:</p>
                  {importResults
                    .filter((r) => !r.success)
                    .map((r) => (
                      <div key={r.rowNum} className="text-xs text-red-600 dark:text-red-400">
                        Fila {r.rowNum} [{r.codigo_control}] — {r.titulo}: {r.error}
                      </div>
                    ))}
                </div>
              )}

              {importResults.filter((r) => r.success).length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Las tareas importadas ya están disponibles en el tab de Tareas de cada control correspondiente.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between flex-shrink-0">
          {step === 'upload' && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Cancelar
            </button>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => {
                  setParsedRows([]);
                  setParseError(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  setStep('upload');
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                ← Volver
              </button>
              <button
                onClick={handleImport}
                disabled={validCount === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span>Importar {validCount} tarea{validCount !== 1 ? 's' : ''}</span>
              </button>
            </>
          )}

          {step === 'done' && (
            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
            >
              Cerrar
            </button>
          )}

          {step === 'importing' && (
            <span className="text-xs text-gray-400 dark:text-gray-500 self-center">
              Por favor espere...
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
